import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import AnimatedBackground from "@/components/site/AnimatedBackground";
import { api } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

function fmtDate(d) {
  try {
    return new Date(d).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/blog")
      .then(({ data }) => setPosts(data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-page" className="relative pt-32">
      <AnimatedBackground variant="editorial" />
      <section className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <p className="font-mono text-sm uppercase tracking-[0.3em] text-brand-blue">The Journal</p>
        <h1 className="mt-6 max-w-4xl font-display text-5xl font-medium leading-[0.95] tracking-tight text-white sm:text-7xl">
          <MaskedLines lines={["Notes from"]} />
          <MaskedLines lines={["the console."]} delay={0.2} lineClassName="text-brand-gradient" />
        </h1>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-16 lg:px-12">
        {loading ? (
          <p className="font-mono text-sm text-slate-500">Loading posts…</p>
        ) : posts.length === 0 ? (
          <p className="font-mono text-sm text-slate-500" data-testid="blog-empty">No posts yet. Check back soon.</p>
        ) : (
          <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
            {posts.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.08} className="h-full">
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group flex h-full flex-col bg-navy transition-colors duration-500 hover:bg-surface"
                >
                  {p.cover_image && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={p.cover_image}
                        alt={p.title}
                        className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:grayscale-0 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-8">
                    <div className="flex items-center justify-between font-mono text-sm uppercase tracking-[0.2em]">
                      <span className="text-brand-magenta">{p.category}</span>
                      <span className="text-slate-400">{fmtDate(p.created_at)}</span>
                    </div>
                    <h3 className="mt-5 font-display text-2xl font-medium leading-snug text-white">{p.title}</h3>
                    <p className="mt-3 flex-1 text-lg font-light leading-relaxed text-slate-400">{p.excerpt}</p>
                    <span className="mt-6 inline-flex items-center gap-2 font-mono text-sm uppercase tracking-[0.2em] text-white">
                      Read <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
